import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Calendar, Check, MessageCircle, Star, TrendingUp, Wallet, X } from 'lucide-react'
import { adminApi, unwrap } from '../../services/api'
import LoadErrorState from '../common/LoadErrorState'
import {
  SERVER_ERROR_MESSAGE,
  SERVER_TIMEOUT_MESSAGE,
  withServerTimeout,
} from '../common/loadErrorUtils'
import { useColors } from '../../hooks/useColors'
import { useToast } from '../../contexts/ToastContext'
import AgentKycReviewDrawer from './AgentKycReviewDrawer'
import { useSocket } from '../../hooks/useSocket'
import { useAuthStore } from '../../store/authStore'

interface AgentRow {
  id: string
  name?: string
  phone?: string
  is_online?: boolean
  today_delivery_count?: number | string
  rating?: number | string
  is_active?: boolean
  zone?: string | null
  kyc_status?: 'pending' | 'verified' | 'rejected'
  verification_status?: string
  documents_uploaded_count?: string | number
  residential_address?: string | null
  city?: string | null
  vehicle_type?: string | null
  vehicle_number?: string | null
  license_number?: string | null
  emergency_contact?: string | null
  paymentDetails?: AgentPaymentDetails | null
}

interface AgentPayload {
  data?: AgentRow[]
}

interface PerformanceData {
  total_deliveries?: number
  this_month?: number
  avg_per_day?: number
  rating?: number
  on_time_percent?: number
  complaints?: number
  active_since?: string
}

interface EarningsData {
  deliveries?: number
  commission?: number
  bonus?: number
  deductions?: number
  net?: number
  pending_payout?: number
}

interface PayoutRow {
  id: string
  agent_id: string
  period_start: string
  period_end: string
  deliveries_count: number
  total_earned: number
  bonus: number
  deductions: number
  net_payout: number
  status: 'pending' | 'paid' | 'held'
  paid_at?: string | null
  payment_method?: string | null
  payment_reference?: string | null
  notes?: string | null
  created_at: string
  agent_name?: string
}

interface AgentPaymentDetails {
  account_holder?: string | null
  account_number?: string | null
  ifsc?: string | null
  upi_id?: string | null
  verification_status?: 'pending' | 'verified' | 'rejected' | string
  rejection_reason?: string | null
}

interface AgentSignedUpEvent {
  agent_id: string
  phone_suffix: string
  name?: string | null
  signed_up_at?: string
}

const PENDING_REVIEW_STATUSES = new Set(['pending', 'documents_missing', 'under_review', 'rejected'])

const getInitials = (name: string | undefined) => {
  if (!name) {
    return 'AG'
  }

  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

const getKycStatus = (agent: AgentRow): 'Verified' | 'Pending' | 'Rejected' => {
  if (agent.verification_status === 'approved' || agent.kyc_status === 'verified') return 'Verified'
  if (agent.verification_status === 'rejected' || agent.kyc_status === 'rejected') return 'Rejected'
  return 'Pending'
}

const isApprovedAgent = (agent: AgentRow) =>
  agent.verification_status === 'approved' || agent.kyc_status === 'verified'

const isPendingReviewAgent = (agent: AgentRow) => {
  if (isApprovedAgent(agent)) return false
  const status = agent.verification_status || agent.kyc_status || 'pending'
  return PENDING_REVIEW_STATUSES.has(status)
}

const openWhatsApp = (phone: string | undefined, message: string, addToast: (msg: string, type?: 'success' | 'error' | 'info') => void) => {
  if (!phone) {
    addToast('Agent phone is not available for WhatsApp.', 'error')
    return
  }

  const cleanPhone = phone.replace(/[\s\-+()]/g, '')
  const phoneWithCountry = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`
  const encodedMessage = encodeURIComponent(message)
  window.open(`https://wa.me/${phoneWithCountry}?text=${encodedMessage}`, '_blank')
}

export default function AgentsTab({ refreshToken = 0 }: { refreshToken?: number }) {
  const user = useAuthStore((state) => state.user)
  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('pending')
  const colors = useColors()
  const { addToast } = useToast()
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedKycAgent, setSelectedKycAgent] = useState<AgentRow | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    zone: '',
  })

  const [selectedAgent, setSelectedAgent] = useState<AgentRow | null>(null)
  const [showDetailPanel, setShowDetailPanel] = useState(false)
  const [performance, setPerformance] = useState<PerformanceData | null>(null)
  const [earnings, setEarnings] = useState<EarningsData | null>(null)
  const [payouts, setPayouts] = useState<PayoutRow[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [recentSignups, setRecentSignups] = useState<AgentSignedUpEvent[]>([])
  const [paymentRejectReason, setPaymentRejectReason] = useState('')
  const [paymentReviewing, setPaymentReviewing] = useState(false)

  const [showPayoutModal, setShowPayoutModal] = useState(false)
  const [payoutForm, setPayoutForm] = useState({
    period_start: '',
    period_end: '',
    bonus: '',
    deductions: '',
    notes: '',
  })
  const [creatingPayout, setCreatingPayout] = useState(false)

  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false)
  const [markPaidTarget, setMarkPaidTarget] = useState<PayoutRow | null>(null)
  const [markPaidForm, setMarkPaidForm] = useState({
    payment_method: 'upi',
    payment_reference: '',
    notes: '',
  })
  const [markingPaid, setMarkingPaid] = useState(false)

  const fetchAgents = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await withServerTimeout(adminApi.getAgents())
      const payload = unwrap<AgentPayload | AgentRow[]>(response)

      if (Array.isArray(payload)) {
        setAgents(payload)
        return
      }

      setAgents(Array.isArray(payload?.data) ? payload.data : [])
    } catch (loadError) {
      setError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchAgents()
  }, [fetchAgents])

  useEffect(() => {
    if (refreshToken > 0) {
      void fetchAgents()
    }
  }, [refreshToken, fetchAgents])

  useSocket({
    'agent:status_changed': (data: { agent_id: string; is_online: boolean }) => {
      setAgents((prev) =>
        prev.map((a) =>
          a.id === data.agent_id ? { ...a, is_online: data.is_online } : a
        )
      )
    },
    'agent:kyc_status_changed': (data: { agent_id: string; verification_status: string }) => {
      setAgents((prev) =>
        prev.map((a) =>
          a.id === data.agent_id
            ? {
                ...a,
                verification_status: data.verification_status,
                kyc_status: data.verification_status === 'approved' ? 'verified' : data.verification_status === 'rejected' ? 'rejected' : a.kyc_status,
              }
            : a
        )
      )
    },
    'agent:registered': () => {
      void fetchAgents()
    },
    'agent:signed_up': (data: AgentSignedUpEvent) => {
      const label = data.name || (data.phone_suffix ? `****${data.phone_suffix}` : 'New agent')
      addToast(`New agent signed up: ${label}`, 'info')
      setRecentSignups((prev) => [data, ...prev].slice(0, 5))
      void fetchAgents()
    },
    'agent:kyc_doc_uploaded': () => {
      void fetchAgents()
    },
    'agent:kyc_doc_reviewed': () => {
      void fetchAgents()
    },
  })

  const stats = useMemo(() => {
    const online = agents.filter((agent) => Boolean(agent.is_online)).length
    const offline = agents.filter((agent) => !agent.is_online).length
    const pending = agents.filter((agent) => agent.verification_status !== 'approved' && agent.kyc_status !== 'verified').length

    return { online, offline, pending }
  }, [agents])

  const visibleAgents = useMemo(() => (
    agents.filter((agent) => (activeTab === 'approved' ? isApprovedAgent(agent) : isPendingReviewAgent(agent)))
  ), [activeTab, agents])

  const handleCreateAgent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    await adminApi.createAgent({
      name: formData.name,
      phone: formData.phone,
      zone: formData.zone || undefined,
    })

    setFormData({ name: '', phone: '', zone: '' })
    setShowCreateModal(false)
    await fetchAgents()
  }

  const handleSuspendAgent = async (id: string) => {
    await adminApi.suspendAgent(id)
    await fetchAgents()
  }

  const handleKycUpdate = async (id: string, status: 'verified' | 'rejected', override?: boolean) => {
    try {
      await adminApi.approveAgentKyc(id, status, override)
      addToast(`Agent KYC ${status}`, 'success')
      await fetchAgents()
    } catch {
      addToast('Failed to update KYC status', 'error')
    }
  }

  const fetchAgentDetails = useCallback(async (agent: AgentRow) => {
    setSelectedAgent(agent)
    setShowDetailPanel(true)
    setDetailLoading(true)
    try {
      const [detailResp, perfResp, earnResp, payoutsResp] = await Promise.all([
        adminApi.getAgentDetail(agent.id),
        adminApi.getAgentPerformance(agent.id),
        adminApi.getAgentEarnings(agent.id),
        adminApi.getPayouts({ agent_id: agent.id, limit: 50 }),
      ])
      setSelectedAgent(unwrap(detailResp) as AgentRow)
      setPerformance(unwrap(perfResp) as PerformanceData)
      setEarnings(unwrap(earnResp) as EarningsData)
      const payoutsPayload = unwrap(payoutsResp) as { data?: PayoutRow[] }
      setPayouts(Array.isArray(payoutsPayload?.data) ? payoutsPayload.data : [])
    } catch {
      // silently fail detail loading
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const handlePaymentReview = async (status: 'verified' | 'rejected') => {
    if (!selectedAgent) return
    const reason = paymentRejectReason.trim()
    if (status === 'rejected' && reason.length < 5) {
      addToast('Enter at least 5 characters for bank rejection reason', 'error')
      return
    }
    setPaymentReviewing(true)
    try {
      await adminApi.verifyAgentPaymentDetails(selectedAgent.id, {
        status,
        ...(status === 'rejected' ? { reason } : {}),
      })
      setPaymentRejectReason('')
      addToast(`Bank details ${status}`, 'success')
      await fetchAgentDetails(selectedAgent)
    } catch {
      addToast('Failed to update bank verification status', 'error')
    } finally {
      setPaymentReviewing(false)
    }
  }

  const handleCreatePayout = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedAgent) return
    setCreatingPayout(true)
    try {
      await adminApi.createPayout({
        agent_id: selectedAgent.id,
        period_start: payoutForm.period_start,
        period_end: payoutForm.period_end,
        bonus: payoutForm.bonus ? Number(payoutForm.bonus) : 0,
        deductions: payoutForm.deductions ? Number(payoutForm.deductions) : 0,
        notes: payoutForm.notes || undefined,
      })
      setShowPayoutModal(false)
      setPayoutForm({ period_start: '', period_end: '', bonus: '', deductions: '', notes: '' })
      await fetchAgentDetails(selectedAgent)
    } finally {
      setCreatingPayout(false)
    }
  }

  const handleMarkPaid = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!markPaidTarget) return
    setMarkingPaid(true)
    try {
      await adminApi.markPayoutPaid(markPaidTarget.id, {
        payment_method: markPaidForm.payment_method,
        payment_reference: markPaidForm.payment_reference || undefined,
        notes: markPaidForm.notes || undefined,
      })
      setShowMarkPaidModal(false)
      setMarkPaidTarget(null)
      setMarkPaidForm({ payment_method: 'upi', payment_reference: '', notes: '' })
      if (selectedAgent) {
        await fetchAgentDetails(selectedAgent)
      }
    } finally {
      setMarkingPaid(false)
    }
  }

  return (
    <div>
      <div
        style={{
          margin: '20px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2
          className="font-display"
          style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          {agents.length} Agents
        </h2>

        <button
          type="button"
          className="font-body"
          onClick={() => setShowCreateModal(true)}
          style={{
            border: 'none',
            background: '#D6B97B',
            color: colors.textPrimary,
            fontSize: '14px',
            fontWeight: 600,
            padding: '10px 20px',
            borderRadius: '10px',
            cursor: 'pointer',
          }}
        >
          + Add Agent
        </button>
      </div>

      <div
        style={{
          margin: '0 32px 20px',
          display: 'flex',
          gap: '12px',
        }}
      >
        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Online
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#15803D' }}>
            {stats.online}
          </p>
        </div>

        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Offline
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: colors.textSecondary }}>
            {stats.offline}
          </p>
        </div>

        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '12px', padding: '16px', border: '1.5px solid #D6B97B' }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Pending Approval
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#D6B97B' }}>
            {stats.pending}
          </p>
        </div>
      </div>

      <div style={{ margin: '0 32px 20px', display: 'flex', gap: '16px', borderBottom: `1px solid ${colors.border}` }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            background: 'none', border: 'none', padding: '12px 0', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            color: activeTab === 'pending' ? colors.textPrimary : colors.textMuted,
            borderBottom: activeTab === 'pending' ? `2px solid ${colors.textPrimary}` : '2px solid transparent',
          }}
        >
          Pending Review
        </button>
        <button
          onClick={() => setActiveTab('approved')}
          style={{
            background: 'none', border: 'none', padding: '12px 0', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            color: activeTab === 'approved' ? colors.textPrimary : colors.textMuted,
            borderBottom: activeTab === 'approved' ? `2px solid ${colors.textPrimary}` : '2px solid transparent',
          }}
        >
          Approved Agents
        </button>
      </div>

      {recentSignups.length > 0 ? (
        <div
          style={{
            margin: '0 32px 20px',
            background: colors.bgCard,
            borderRadius: '16px',
            border: `1.5px solid ${colors.border}`,
            padding: '16px 18px',
          }}
        >
          <p
            className="font-body"
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Recent Sign-ups
          </p>
          <div style={{ display: 'grid', gap: '8px' }}>
            {recentSignups.map((signup) => (
              <div
                key={`${signup.agent_id}-${signup.signed_up_at ?? ''}`}
                className="font-body"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  background: colors.bgSecondary,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    {signup.name || 'New agent'}
                  </span>
                  <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                    {signup.phone_suffix ? `****${signup.phone_suffix}` : 'Phone unavailable'}
                  </span>
                </div>
                <span style={{ fontSize: '11px', color: colors.textMuted }}>
                  {signup.signed_up_at ? new Date(signup.signed_up_at).toLocaleString() : 'Just now'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          margin: '0 32px 32px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
              {['Photo', 'Name', 'Phone', 'Status', "Today's Tasks", 'Rating', 'KYC Status', 'Actions'].map((header) => (
                <th key={header} className="font-body" style={{ padding: '16px 20px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left' }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {error && !loading ? (
              <tr>
                <td colSpan={8}>
                  <LoadErrorState message={error} onRetry={() => void fetchAgents()} margin="24px" />
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={8} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    Loading agents...
                  </p>
                </td>
              </tr>
            ) : visibleAgents.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    No agents found.
                  </p>
                </td>
              </tr>
            ) : (
              visibleAgents.map((agent) => {
              const kycStatus = getKycStatus(agent)

              return (
                <tr key={agent.id} style={{ borderBottom: `0.5px solid ${colors.surface}` }}>
                  <td style={{ padding: '16px 20px' }}>
                    <div className="font-body" style={{ width: '36px', height: '36px', borderRadius: '999px', background: '#D6B97B', color: colors.textPrimary, display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 600 }}>
                      {getInitials(agent.name)}
                    </div>
                  </td>
                  <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                    {agent.name || 'Agent'}
                  </td>
                  <td className="font-body" style={{ padding: '16px 20px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{agent.phone || '-'}</span>
                      <button
                        type="button"
                        aria-label="Send WhatsApp"
                        onClick={() => {
                          openWhatsApp(agent.phone, `Hi ${agent.name || 'Agent'}, this is Rapidry Admin.`, addToast)
                        }}
                        style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#25D366',
                          color: colors.bgCard,
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = '#1EAD54'
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = '#25D366'
                        }}
                      >
                        <MessageCircle size={12} />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <span className="font-body" style={{ background: agent.is_online ? '#F0FDF4' : '#F3F4F6', color: agent.is_online ? '#15803D' : '#6B7280', border: `1px solid ${agent.is_online ? '#86EFAC' : '#D1D5DB'}`, borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '999px', background: agent.is_online ? '#15803D' : '#6B7280' }} />
                      {agent.is_online ? 'Online' : 'Offline'}
                    </span>
                  </td>
                  <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                    {Number(agent.today_delivery_count || 0)}
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Star size={16} color="#D6B97B" fill="#D6B97B" />
                      <span className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                        {Number(agent.rating || 0).toFixed(1)}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <span className="font-body" style={{ borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px', background: kycStatus === 'Verified' ? '#F0FDF4' : kycStatus === 'Pending' ? 'rgba(214,185,123,0.12)' : '#FEF2F2', color: kycStatus === 'Verified' ? '#15803D' : kycStatus === 'Pending' ? '#D6B97B' : '#991B1B', border: `1px solid ${kycStatus === 'Verified' ? '#86EFAC' : kycStatus === 'Pending' ? '#D6B97B' : '#FCA5A5'}` }}>
                      {kycStatus === 'Verified' ? <Check size={14} strokeWidth={2.5} /> : null}
                      {kycStatus}
                    </span>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button type="button" className="font-body" onClick={() => { void fetchAgentDetails(agent) }} style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary, background: 'transparent', cursor: 'pointer' }} onMouseEnter={(event) => { event.currentTarget.style.background = colors.surface }} onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent' }}>
                        View
                      </button>
                      {getKycStatus(agent) !== 'Verified' && Number(agent.documents_uploaded_count || 0) > 0 && user?.permissions?.includes('agents.create') ? (
                        <button onClick={() => setSelectedKycAgent(agent)}
                          style={{ border: '1px solid #D6B97B', borderRadius: '6px', 
                                   padding: '4px 10px', fontSize: '12px', fontWeight: 600, 
                                   color: '#D6B97B', background: 'transparent', cursor: 'pointer' }}>
                          Review KYC
                        </button>
                      ) : null}
                      {getKycStatus(agent) !== 'Verified' ? (
                        <>
                          <button onClick={() => {
                            if (window.confirm('You are bypassing per-doc review. Continue?')) {
                              void handleKycUpdate(agent.id, 'verified', true)
                            }
                          }}
                            style={{ border: '1px solid #86EFAC', borderRadius: '6px', 
                                     padding: '4px 10px', fontSize: '12px', fontWeight: 600, 
                                     color: '#15803D', background: 'transparent', cursor: 'pointer' }}>
                            Manual override (Approve)
                          </button>
                          <button onClick={() => {
                            if (window.confirm('You are bypassing per-doc review. Continue?')) {
                              void handleKycUpdate(agent.id, 'rejected', true)
                            }
                          }}
                            style={{ border: '1px solid #FCA5A5', borderRadius: '6px', 
                                     padding: '4px 10px', fontSize: '12px', fontWeight: 600, 
                                     color: '#991B1B', background: 'transparent', cursor: 'pointer' }}>
                            Manual override (Reject)
                          </button>
                        </>
                      ) : null}
                      <button type="button" className="font-body" onClick={() => { void handleSuspendAgent(agent.id) }} style={{ border: '1px solid #FCA5A5', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, color: '#991B1B', background: 'transparent', cursor: 'pointer' }} onMouseEnter={(event) => { event.currentTarget.style.background = '#FEF2F2' }} onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent' }}>
                        Suspend
                      </button>
                    </div>
                  </td>
                </tr>
              )
            }))}
          </tbody>
        </table>
      </div>

      {showCreateModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <form
            onSubmit={(event) => {
              void handleCreateAgent(event)
            }}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '420px',
              background: colors.bgCard,
              borderRadius: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '28px',
            }}
          >
            <h3 className="font-display" style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
              Add Agent
            </h3>

            <div style={{ display: 'grid', gap: '14px' }}>
              <input required value={formData.name} onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))} placeholder="Agent name" className="font-body" style={{ height: '44px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 14px', fontSize: '14px' }} />
              <input required value={formData.phone} onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))} placeholder="Phone number" className="font-body" style={{ height: '44px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 14px', fontSize: '14px' }} />
              <input value={formData.zone} onChange={(event) => setFormData((prev) => ({ ...prev, zone: event.target.value }))} placeholder="Zone" className="font-body" style={{ height: '44px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 14px', fontSize: '14px' }} />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button type="button" className="font-body" onClick={() => setShowCreateModal(false)} style={{ flex: 1, height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', background: 'transparent', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" className="font-body" style={{ flex: 1, height: '42px', border: 'none', borderRadius: '10px', background: '#D6B97B', fontSize: '14px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer' }}>
                Create Agent
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {/* Agent Detail Panel */}
      {showDetailPanel && selectedAgent ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 70,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.3)',
            }}
            onClick={() => setShowDetailPanel(false)}
          />
          <div
            style={{
              position: 'relative',
              width: '520px',
              maxWidth: '100%',
              height: '100%',
              background: colors.bgCard,
              boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Panel Header */}
            <div style={{ padding: '24px 28px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
                  {selectedAgent.name || 'Agent'}
                </h3>
                <p className="font-body" style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textMuted }}>
                  {selectedAgent.phone}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDetailPanel(false)}
                style={{ width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bgCard, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
              >
                <X size={18} color={colors.textSecondary} />
              </button>
            </div>

            {/* Panel Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
              {detailLoading ? (
                <p className="font-body" style={{ color: colors.textMuted, fontSize: '14px' }}>Loading details...</p>
              ) : (
                <div style={{ display: 'grid', gap: '24px' }}>
                  {/* Profile, Vehicle, And Bank */}
                  <div>
                    <p className="font-body" style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Profile & KYC Details
                    </p>
                    <div style={{ background: colors.bgSecondary, borderRadius: '14px', padding: '18px', border: `1.5px solid ${colors.border}`, display: 'grid', gap: '14px' }}>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>Residential Address</p>
                        <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textPrimary }}>{selectedAgent.residential_address || 'Not added'}</p>
                        <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>{selectedAgent.city || selectedAgent.zone || 'City / zone not added'}</p>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                        <div>
                          <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>Vehicle</p>
                          <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textPrimary }}>{selectedAgent.vehicle_type || 'Not added'}</p>
                          <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>{selectedAgent.vehicle_number || 'Number not added'}</p>
                        </div>
                        <div>
                          <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>License / Emergency</p>
                          <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textPrimary }}>{selectedAgent.license_number || 'License not added'}</p>
                          <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>{selectedAgent.emergency_contact || 'Emergency contact not added'}</p>
                        </div>
                      </div>
                      <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '14px' }}>
                        <p className="font-body" style={{ margin: '0 0 8px', fontSize: '12px', color: colors.textMuted }}>Bank Details</p>
                        {selectedAgent.paymentDetails ? (
                          <div style={{ display: 'grid', gap: '8px' }}>
                            <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textPrimary }}>{selectedAgent.paymentDetails.account_holder || 'Holder not added'}</p>
                            <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>{selectedAgent.paymentDetails.account_number || 'Account not added'} · IFSC {selectedAgent.paymentDetails.ifsc || '-'}</p>
                            {selectedAgent.paymentDetails.upi_id ? <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>UPI {selectedAgent.paymentDetails.upi_id}</p> : null}
                            <span className="font-body" style={{ justifySelf: 'start', borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize', background: selectedAgent.paymentDetails.verification_status === 'verified' ? '#F0FDF4' : selectedAgent.paymentDetails.verification_status === 'rejected' ? '#FEF2F2' : 'rgba(214,185,123,0.12)', color: selectedAgent.paymentDetails.verification_status === 'verified' ? '#15803D' : selectedAgent.paymentDetails.verification_status === 'rejected' ? '#991B1B' : '#D6B97B', border: `1px solid ${selectedAgent.paymentDetails.verification_status === 'verified' ? '#86EFAC' : selectedAgent.paymentDetails.verification_status === 'rejected' ? '#FCA5A5' : '#D6B97B'}` }}>
                              {selectedAgent.paymentDetails.verification_status || 'pending'}
                            </span>
                            {selectedAgent.paymentDetails.rejection_reason ? <p className="font-body" style={{ margin: 0, fontSize: '12px', color: '#991B1B' }}>{selectedAgent.paymentDetails.rejection_reason}</p> : null}
                            {user?.permissions?.includes('agents.create') ? (
                              <div style={{ display: 'grid', gap: '8px', marginTop: '4px' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button type="button" disabled={paymentReviewing} onClick={() => void handlePaymentReview('verified')} className="font-body" style={{ border: '1px solid #86EFAC', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: 600, color: '#15803D', background: 'transparent', cursor: paymentReviewing ? 'not-allowed' : 'pointer' }}>Verify bank</button>
                                  <button type="button" disabled={paymentReviewing || paymentRejectReason.trim().length < 5} onClick={() => void handlePaymentReview('rejected')} className="font-body" style={{ border: '1px solid #FCA5A5', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: 600, color: '#991B1B', background: 'transparent', cursor: paymentReviewing || paymentRejectReason.trim().length < 5 ? 'not-allowed' : 'pointer', opacity: paymentRejectReason.trim().length < 5 ? 0.5 : 1 }}>Reject bank</button>
                                </div>
                                <input value={paymentRejectReason} onChange={(event) => setPaymentRejectReason(event.target.value)} className="font-body" style={{ height: '36px', border: `1.5px solid ${colors.border}`, borderRadius: '8px', padding: '0 10px', fontSize: '13px' }} placeholder="Bank rejection reason" />
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>No bank details submitted.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Performance Stats */}
                  <div>
                    <p className="font-body" style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Performance
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                      <div style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <TrendingUp size={16} color="#D6B97B" />
                          <span className="font-body" style={{ fontSize: '11px', fontWeight: 600, color: colors.textMuted }}>Total Deliveries</span>
                        </div>
                        <p className="font-display" style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
                          {performance?.total_deliveries ?? '-'}
                        </p>
                      </div>
                      <div style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <Calendar size={16} color="#D6B97B" />
                          <span className="font-body" style={{ fontSize: '11px', fontWeight: 600, color: colors.textMuted }}>This Month</span>
                        </div>
                        <p className="font-display" style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
                          {performance?.this_month ?? '-'}
                        </p>
                      </div>
                      <div style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <Star size={16} color="#D6B97B" />
                          <span className="font-body" style={{ fontSize: '11px', fontWeight: 600, color: colors.textMuted }}>Rating</span>
                        </div>
                        <p className="font-display" style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
                          {performance?.rating?.toFixed(1) ?? '-'}
                        </p>
                      </div>
                      <div style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <TrendingUp size={16} color="#D6B97B" />
                          <span className="font-body" style={{ fontSize: '11px', fontWeight: 600, color: colors.textMuted }}>On-Time %</span>
                        </div>
                        <p className="font-display" style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
                          {performance?.on_time_percent != null ? `${performance.on_time_percent}%` : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Earnings Summary */}
                  <div>
                    <p className="font-body" style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Earnings Summary
                    </p>
                    <div style={{ background: colors.bgSecondary, borderRadius: '14px', padding: '18px', border: `1.5px solid ${colors.border}`, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>Total Earned</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>
                          ₹{earnings?.net?.toLocaleString() ?? '-'}
                        </p>
                      </div>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>Pending Payout</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#D6B97B' }}>
                          ₹{earnings?.pending_payout?.toLocaleString() ?? '-'}
                        </p>
                      </div>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>Deliveries</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>
                          {earnings?.deliveries ?? '-'}
                        </p>
                      </div>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '12px', color: colors.textMuted }}>Commission</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>
                          ₹{earnings?.commission?.toLocaleString() ?? '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Create Payout Button */}
                  <button
                    type="button"
                    className="font-body"
                    onClick={() => setShowPayoutModal(true)}
                    style={{
                      height: '44px',
                      borderRadius: '10px',
                      border: 'none',
                      background: colors.textPrimary,
                      color: colors.bgCard,
                      fontWeight: 700,
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <Wallet size={16} /> Create Payout
                  </button>

                  {/* Payout History */}
                  <div>
                    <p className="font-body" style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Payout History
                    </p>
                    {payouts.length === 0 ? (
                      <p className="font-body" style={{ color: colors.textMuted, fontSize: '13px' }}>No payouts yet.</p>
                    ) : (
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {payouts.map((payout) => (
                          <div key={payout.id} style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '14px 16px', border: `1.5px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                            <div>
                              <p className="font-body" style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                                {payout.period_start} → {payout.period_end}
                              </p>
                              <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                                {payout.deliveries_count} deliveries · ₹{payout.net_payout}
                              </p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="font-body" style={{
                                borderRadius: '999px',
                                padding: '4px 10px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: payout.status === 'paid' ? '#F0FDF4' : payout.status === 'held' ? '#FEF2F2' : 'rgba(214,185,123,0.12)',
                                color: payout.status === 'paid' ? '#15803D' : payout.status === 'held' ? '#991B1B' : '#D6B97B',
                                border: `1px solid ${payout.status === 'paid' ? '#86EFAC' : payout.status === 'held' ? '#FCA5A5' : '#D6B97B'}`,
                                textTransform: 'capitalize',
                              }}>
                                {payout.status}
                              </span>
                              {payout.status === 'pending' ? (
                                <button
                                  type="button"
                                  className="font-body"
                                  onClick={() => {
                                    setMarkPaidTarget(payout)
                                    setShowMarkPaidModal(true)
                                  }}
                                  style={{
                                    border: `1px solid ${colors.textPrimary}`,
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: colors.textPrimary,
                                    background: 'transparent',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Mark Paid
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Create Payout Modal */}
      {showPayoutModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 80,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowPayoutModal(false)}
        >
          <form
            onSubmit={(event) => { void handleCreatePayout(event) }}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '440px',
              maxWidth: '100%',
              background: colors.bgCard,
              borderRadius: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '28px',
            }}
          >
            <h3 className="font-display" style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
              Create Payout
            </h3>
            <div style={{ display: 'grid', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Period Start</label>
                  <input required type="date" value={payoutForm.period_start} onChange={(event) => setPayoutForm((prev) => ({ ...prev, period_start: event.target.value }))} className="font-body" style={{ height: '44px', width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px', fontSize: '14px' }} />
                </div>
                <div>
                  <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Period End</label>
                  <input required type="date" value={payoutForm.period_end} onChange={(event) => setPayoutForm((prev) => ({ ...prev, period_end: event.target.value }))} className="font-body" style={{ height: '44px', width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px', fontSize: '14px' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Bonus (₹)</label>
                  <input type="number" min="0" value={payoutForm.bonus} onChange={(event) => setPayoutForm((prev) => ({ ...prev, bonus: event.target.value }))} className="font-body" style={{ height: '44px', width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px', fontSize: '14px' }} placeholder="0" />
                </div>
                <div>
                  <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Deductions (₹)</label>
                  <input type="number" min="0" value={payoutForm.deductions} onChange={(event) => setPayoutForm((prev) => ({ ...prev, deductions: event.target.value }))} className="font-body" style={{ height: '44px', width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px', fontSize: '14px' }} placeholder="0" />
                </div>
              </div>
              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Notes</label>
                <textarea value={payoutForm.notes} onChange={(event) => setPayoutForm((prev) => ({ ...prev, notes: event.target.value }))} className="font-body" style={{ width: '100%', minHeight: '80px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '10px 12px', fontSize: '14px', resize: 'vertical' }} placeholder="Optional notes..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button type="button" className="font-body" onClick={() => setShowPayoutModal(false)} style={{ flex: 1, height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', background: 'transparent', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={creatingPayout} className="font-body" style={{ flex: 1, height: '42px', border: 'none', borderRadius: '10px', background: colors.textPrimary, fontSize: '14px', fontWeight: 600, color: colors.bgCard, cursor: creatingPayout ? 'not-allowed' : 'pointer', opacity: creatingPayout ? 0.6 : 1 }}>
                {creatingPayout ? 'Creating...' : 'Create Payout'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {/* Mark Paid Modal */}
      {showMarkPaidModal && markPaidTarget ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 80,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowMarkPaidModal(false)}
        >
          <form
            onSubmit={(event) => { void handleMarkPaid(event) }}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '440px',
              maxWidth: '100%',
              background: colors.bgCard,
              borderRadius: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '28px',
            }}
          >
            <h3 className="font-display" style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
              Mark Payout as Paid
            </h3>
            <div style={{ marginBottom: '16px', background: colors.bgSecondary, borderRadius: '10px', padding: '12px 14px' }}>
              <p className="font-body" style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                {markPaidTarget.period_start} → {markPaidTarget.period_end}
              </p>
              <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                ₹{markPaidTarget.net_payout} · {markPaidTarget.deliveries_count} deliveries
              </p>
            </div>
            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Payment Method</label>
                <select value={markPaidForm.payment_method} onChange={(event) => setMarkPaidForm((prev) => ({ ...prev, payment_method: event.target.value }))} className="font-body" style={{ height: '44px', width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px', fontSize: '14px', background: colors.bgCard }}>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Payment Reference</label>
                <input value={markPaidForm.payment_reference} onChange={(event) => setMarkPaidForm((prev) => ({ ...prev, payment_reference: event.target.value }))} className="font-body" style={{ height: '44px', width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px', fontSize: '14px' }} placeholder="UPI txn ID / Ref #" />
              </div>
              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>Notes</label>
                <textarea value={markPaidForm.notes} onChange={(event) => setMarkPaidForm((prev) => ({ ...prev, notes: event.target.value }))} className="font-body" style={{ width: '100%', minHeight: '60px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '10px 12px', fontSize: '14px', resize: 'vertical' }} placeholder="Optional notes..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button type="button" className="font-body" onClick={() => setShowMarkPaidModal(false)} style={{ flex: 1, height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', background: 'transparent', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={markingPaid} className="font-body" style={{ flex: 1, height: '42px', border: 'none', borderRadius: '10px', background: colors.textPrimary, fontSize: '14px', fontWeight: 600, color: colors.bgCard, cursor: markingPaid ? 'not-allowed' : 'pointer', opacity: markingPaid ? 0.6 : 1 }}>
                {markingPaid ? 'Saving...' : 'Mark as Paid'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {selectedKycAgent ? (
        <AgentKycReviewDrawer
          agent={selectedKycAgent}
          onClose={() => setSelectedKycAgent(null)}
          onUpdate={() => void fetchAgents()}
        />
      ) : null}
    </div>
  )
}
